import React, { useState } from 'react';
import { Alert, Form, Input, Modal, Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';

export interface EpcWbsLibraryImportWizardProps {
  open: boolean;
  onClose: () => void;
  libraryId: string;
  /** When set, merges into existing package via new import (creates sibling if library import) */
  packageId?: string;
  onCompleted: () => void;
}

export const EpcWbsLibraryImportWizard: React.FC<EpcWbsLibraryImportWizardProps> = ({
  open,
  onClose,
  libraryId,
  onCompleted,
}) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);

  const importM = useMutation({
    mutationFn: (v: { packageCode: string; packageName: string }) => {
      if (!file) throw new Error('Select an Excel file');
      return epcWbsLibraryService.importPackage(libraryId, file, v.packageCode, v.packageName);
    },
    onSuccess: () => {
      message.success('Package imported');
      form.resetFields();
      setFile(null);
      onCompleted();
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <Modal
      title="Import WBS package from Excel"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={importM.isPending}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Upload a package export (.xlsx) with WBS nodes and CBS cost templates."
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => importM.mutate(v)}
      >
        <Form.Item name="packageCode" label="Package code" rules={[{ required: true }]}>
          <Input placeholder="PKG-IMPORT-01" />
        </Form.Item>
        <Form.Item name="packageName" label="Package name" rules={[{ required: true }]}>
          <Input placeholder="Imported work package" />
        </Form.Item>
        <Form.Item label="Excel file" required>
          <Upload.Dragger
            maxCount={1}
            accept=".xlsx,.xls"
            beforeUpload={(f) => {
              setFile(f);
              return false;
            }}
            onRemove={() => setFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag package Excel file</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};
